<!--
* @Author: AI Assistant
* @Date: 2026-01-12
* @Description: API Pricing 页面 - 价格与计费
-->
<template>
    <div
        class="max-w-[1008px] mx-auto px-7 py-7 text-white max-md:px-[14px] max-md:py-[14px]"
    >
        <h1 class="sr-only">{{ t('api.pricingTitle') }}</h1>
        <!-- 页面标题 -->
        <div class="mb-8 max-md:mb-[21px]">
            <h2
                class="text-[21px] font-semibold leading-7 text-white m-0 mb-2 max-md:text-[17.5px] max-md:leading-[1.4em] max-md:mb-[1.75px]"
            >
                {{ t('api.pricingTitle') }}
            </h2>
            <p
                class="text-sm font-normal leading-[21px] text-[#a1a1aa] m-0 max-md:text-[11px] max-md:leading-[1.5em]"
            >
                {{ t('api.pricingDesc') }}
            </p>
        </div>

        <!-- 当前金币余额卡片 -->
        <div
            class="relative bg-[#1c1c20] border border-white/[0.06] rounded-[14px] p-[29px] mb-8 max-md:p-[17.5px] max-md:mb-0"
        >
            <div
                class="flex items-center gap-2 text-xs font-medium text-[#a1a1aa] mb-[3px] max-md:text-[10px] max-md:leading-[1.5em] max-md:uppercase max-md:tracking-[0.05em] max-md:font-semibold max-md:mb-[7px]"
            >
                <span>{{ t('api.currentBalance') }}</span>
                <span class="status-dot max-md:hidden"></span>
            </div>
            <div class="flex items-end gap-1">
                <span
                    class="font-['Inter'] text-[42px] font-bold leading-[50.5px] tracking-[-0.025em] text-white max-md:text-[26.3px] max-md:leading-[1.198em] max-md:tracking-[-0.025em]"
                >
                    {{ userCredits }}
                </span>
                <span
                    class="text-[21px] font-normal leading-7 tracking-[-0.05em] text-[#71717a] mb-[11px] max-md:text-[12.3px] max-md:leading-[1.423em] max-md:tracking-[-0.053em] max-md:text-[#52525b] max-md:mb-[11.5px]"
                >
                    {{ t('api.credits') }}
                </span>
            </div>
        </div>

        <!-- 购买积分 -->
        <div class="mb-8 max-md:mb-0 max-md:mt-[22.5px]">
            <h3
                class="text-[17.5px] font-semibold leading-[24.5px] text-white m-0 mb-[14px] max-md:text-[14px] max-md:leading-[1.5em] max-md:pt-[7px] max-md:pb-0 max-md:mb-[14px]"
            >
                {{ t('api.buyCredits') }}
            </h3>
            <!-- 套餐卡片网格 - 2 列布局 -->
            <div
                class="packages-grid grid grid-cols-2 gap-[14px] max-md:grid-cols-1 max-md:gap-[10.5px]"
            >
                <!-- 加载中状态 - 骨架屏卡片 -->
                <template v-if="loading">
                    <div
                        v-for="i in 4"
                        :key="`skeleton-${i}`"
                        class="package-card pointer-events-none"
                    >
                        <div
                            class="flex items-baseline justify-between gap-[12px]"
                        >
                            <div
                                class="skeleton-shimmer h-[38px] w-[120px] rounded max-md:h-[30px] max-md:w-[90px]"
                            ></div>
                            <div
                                class="skeleton-shimmer h-[18px] w-[80px] rounded"
                            ></div>
                        </div>
                        <div class="mt-[12px] mb-[18px]">
                            <div
                                class="skeleton-shimmer h-[15px] w-[110px] rounded"
                            ></div>
                            <div
                                class="skeleton-shimmer mt-[8px] h-[15px] w-[95px] rounded"
                            ></div>
                        </div>
                        <div
                            class="skeleton-shimmer h-[42px] w-full rounded-[999px] max-md:h-[38px]"
                        ></div>
                    </div>
                </template>

                <!-- 无数据状态 -->
                <div
                    v-else-if="packages.length === 0"
                    class="col-span-2 flex items-center justify-center py-12 text-[#a1a1aa] max-md:col-span-1"
                >
                    <span>{{ t('api.noPackages') }}</span>
                </div>

                <!-- 套餐卡片 -->
                <template v-else>
                    <div
                        v-for="(pkg, index) in packages"
                        :key="pkg.id"
                        class="package-card"
                        :class="{ featured: index === 1 }"
                    >
                        <!-- 价格行 - 总价(左) + 积分(右) -->
                        <div
                            class="flex items-baseline justify-between gap-[12px]"
                        >
                            <div
                                class="font-['Inter'] text-[38px] font-bold leading-[1.1em] tracking-[-0.02em] text-white max-md:text-[30px]"
                            >
                                {{ pkg.price }}
                            </div>
                            <div class="package-credits-inline">
                                <span
                                    class="package-credits-value font-['Menlo'] text-white font-medium"
                                >
                                    {{ pkg.credits }}
                                </span>
                                <span class="package-credits-label">{{
                                    t('api.credits')
                                }}</span>
                            </div>
                        </div>
                        <!-- 说明行 - 约等于图片数 / 视频数 -->
                        <div
                            class="mt-[12px] mb-[18px] flex flex-col gap-[6px]"
                        >
                            <div class="package-info-line">
                                <span
                                    class="font-['Inter'] text-white font-medium"
                                >
                                    ≈ {{ pkg.imageEstimate }}
                                </span>
                                <span class="capitalize">{{
                                    t('api.imageEstimateUnit')
                                }}</span>
                                <el-tooltip
                                    placement="top"
                                    effect="dark"
                                    popper-class="model-rates-tooltip-popper"
                                    :show-after="120"
                                >
                                    <template #content>
                                        <div
                                            class="model-rates-tooltip-content"
                                        >
                                            <p>
                                                {{
                                                    t(
                                                        'api.imageEstimateTooltip'
                                                    )
                                                }}
                                            </p>
                                        </div>
                                    </template>
                                    <el-icon
                                        class="package-info-tooltip-icon"
                                        :aria-label="
                                            t('api.imageEstimateTooltipLabel')
                                        "
                                    >
                                        <InfoFilled />
                                    </el-icon>
                                </el-tooltip>
                            </div>
                            <div class="package-info-line">
                                <span
                                    class="font-['Inter'] text-white font-medium"
                                >
                                    ≈ {{ pkg.videoEstimate }}
                                </span>
                                <span class="capitalize">{{
                                    t('api.videoEstimateUnit')
                                }}</span>
                                <el-tooltip
                                    placement="top"
                                    effect="dark"
                                    popper-class="model-rates-tooltip-popper"
                                    :show-after="120"
                                >
                                    <template #content>
                                        <div
                                            class="model-rates-tooltip-content"
                                        >
                                            <p>
                                                {{
                                                    t(
                                                        'api.videoEstimateTooltip'
                                                    )
                                                }}
                                            </p>
                                        </div>
                                    </template>
                                    <el-icon
                                        class="package-info-tooltip-icon"
                                        :aria-label="
                                            t('api.videoEstimateTooltipLabel')
                                        "
                                    >
                                        <InfoFilled />
                                    </el-icon>
                                </el-tooltip>
                            </div>
                        </div>
                        <!-- 立即购买按钮 -->
                        <button
                            class="package-buy-btn"
                            :class="{ 'is-featured': index === 1 }"
                            @click="handlePurchase(pkg)"
                        >
                            {{ t('api.buyNow') }}
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <!-- 企业用量与定制报价入口 -->
        <section
            class="enterprise-pricing"
            aria-labelledby="enterprise-pricing-title"
        >
            <div class="enterprise-pricing-copy">
                <p class="enterprise-pricing-eyebrow">
                    {{ t('api.businessInquiry') }}
                </p>
                <h3 id="enterprise-pricing-title">
                    {{ t('api.enterprisePricingTitle') }}
                </h3>
                <p class="enterprise-pricing-desc">
                    {{ t('api.enterprisePricingDesc') }}
                </p>
                <div class="enterprise-pricing-benefits" aria-hidden="true">
                    <span>{{ t('api.higherQuotaTitle') }}</span>
                    <span>{{ t('api.lowerTokenPriceTitle') }}</span>
                    <span>{{ t('api.earlyAccessTitle') }}</span>
                </div>
            </div>
            <NuxtLink
                :to="localePath('/api/contact')"
                class="enterprise-pricing-link"
            >
                {{ t('api.enterprisePricingCta') }}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                </svg>
            </NuxtLink>
        </section>

        <!-- 模型消耗费率 -->
        <div class="mb-8 max-md:mb-0 max-md:mt-[20px]">
            <div class="model-rates-heading">
                <h3
                    class="text-[17.5px] font-semibold leading-[24.5px] text-white m-0 max-md:text-[14px] max-md:leading-[1.5em]"
                >
                    {{ t('api.modelRates') }}
                </h3>
                <el-tooltip
                    placement="top"
                    effect="dark"
                    popper-class="model-rates-tooltip-popper"
                    :show-after="120"
                >
                    <template #content>
                        <div class="model-rates-tooltip-content">
                            <p>{{ t('api.modelRatesTooltipImage') }}</p>
                            <p>{{ t('api.modelRatesTooltipVideo') }}</p>
                            <p>{{ t('api.modelRatesTooltipHailuo03') }}</p>
                            <p>{{ t('api.modelRatesTooltipReference') }}</p>
                        </div>
                    </template>
                    <el-icon
                        class="model-rates-tooltip-icon"
                        tabindex="0"
                        :aria-label="t('api.modelRatesTooltipLabel')"
                    >
                        <InfoFilled />
                    </el-icon>
                </el-tooltip>
            </div>
            <!-- 视频 / 图片 切换 -->
            <div
                class="rate-tabs"
                :class="{ 'is-image': activeRateTab === 'image' }"
            >
                <span class="rate-tab-slider" aria-hidden="true"></span>
                <button
                    type="button"
                    class="rate-tab"
                    :class="{ 'is-active': activeRateTab === 'video' }"
                    @click="activeRateTab = 'video'"
                >
                    {{ t('api.rateTabVideo') }}
                </button>
                <button
                    type="button"
                    class="rate-tab"
                    :class="{ 'is-active': activeRateTab === 'image' }"
                    @click="activeRateTab = 'image'"
                >
                    {{ t('api.rateTabImage') }}
                </button>
            </div>
            <!-- 品牌 / 产品线筛选 -->
            <div class="brand-tabs">
                <button
                    type="button"
                    class="brand-tab"
                    :class="{ 'is-active': activeBrand === '' }"
                    @click="activeBrand = ''"
                >
                    {{ t('api.brandAll') }}
                </button>
                <button
                    v-for="b in availableBrands"
                    :key="b"
                    type="button"
                    class="brand-tab"
                    :class="{ 'is-active': activeBrand === b }"
                    @click="activeBrand = b"
                >
                    {{ b }}
                </button>
            </div>
            <div
                class="bg-[#1c1c20] border border-[#27272a] rounded-[10.5px] overflow-hidden"
            >
                <div class="overflow-x-auto max-md:pb-[8px]">
                    <!-- 视频费率 -->
                    <table
                        v-if="activeRateTab === 'video'"
                        class="w-full border-collapse max-md:min-w-[560px]"
                    >
                        <thead>
                            <tr class="bg-black/50 border-b border-[#27272a]">
                                <th class="col-model">
                                    {{ t('api.modelLabel') }}
                                </th>
                                <th>{{ t('api.modeLabel') }}</th>
                                <th>{{ t('api.durationLabel') }}</th>
                                <th>{{ t('api.resolutionLabel') }}</th>
                                <th>{{ t('api.typeLabel') }}</th>
                                <th class="col-consumption">
                                    {{ t('api.consumptionLabel') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in videoRateRows"
                                :key="index"
                                class="border-t border-[#27272a] first:border-t-0"
                            >
                                <td
                                    v-if="item.spans.title.show"
                                    :rowspan="item.spans.title.span"
                                    class="model-name rate-group-cell"
                                >
                                    {{ item.row.title }}
                                </td>
                                <td
                                    v-if="item.spans.mode.show"
                                    :rowspan="item.spans.mode.span"
                                    class="rate-group-cell"
                                >
                                    {{ modeText(item.row.mode) }}
                                </td>
                                <td
                                    v-if="item.spans.duration.show"
                                    :rowspan="item.spans.duration.span"
                                    class="rate-group-cell"
                                >
                                    {{
                                        item.row.duration
                                            ? item.row.duration + 's'
                                            : '—'
                                    }}
                                </td>
                                <td
                                    v-if="item.spans.resolution.show"
                                    :rowspan="item.spans.resolution.span"
                                    class="rate-group-cell"
                                >
                                    {{ resolutionText(item.row.resolution) }}
                                </td>
                                <td>{{ typeText(item.row.typeKey) }}</td>
                                <td class="model-consumption">
                                    {{ formatCredits(item.row.credits) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <!-- 图片费率 -->
                    <table
                        v-else
                        class="w-full border-collapse max-md:min-w-[560px]"
                    >
                        <thead>
                            <tr class="bg-black/50 border-b border-[#27272a]">
                                <th class="col-model">
                                    {{ t('api.modelLabel') }}
                                </th>
                                <th>{{ t('api.typeLabel') }}</th>
                                <th>{{ t('api.qualityLabel') }}</th>
                                <th>{{ t('api.ratioLabel') }}</th>
                                <th>{{ t('api.resolutionLabel') }}</th>
                                <th class="col-consumption">
                                    {{ t('api.consumptionLabel') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in imageRateRows"
                                :key="index"
                                class="border-t border-[#27272a] first:border-t-0"
                            >
                                <td
                                    v-if="item.spans.title.show"
                                    :rowspan="item.spans.title.span"
                                    class="model-name rate-group-cell"
                                >
                                    {{ item.row.title }}
                                </td>
                                <td
                                    v-if="item.spans.typeKey.show"
                                    :rowspan="item.spans.typeKey.span"
                                    class="rate-group-cell"
                                >
                                    {{ typeText(item.row.typeKey) }}
                                </td>
                                <td
                                    v-if="item.spans.quality.show"
                                    :rowspan="item.spans.quality.span"
                                    class="rate-group-cell"
                                >
                                    {{ qualityText(item.row.quality) }}
                                </td>
                                <td
                                    v-if="item.spans.ratio.show"
                                    :rowspan="item.spans.ratio.span"
                                    class="rate-group-cell"
                                >
                                    {{ item.row.ratio || '—' }}
                                </td>
                                <td>{{ item.row.resolution || '—' }}</td>
                                <td class="model-consumption">
                                    {{ formatCredits(item.row.credits) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- 子路由内容渲染 -->
        <div class="mt-[30px] pt-[30px] border-t border-[#2a2a2a]" v-if="false">
            <NuxtPage />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useApiStore } from '~/stores/api'
import { useUserStore } from '~/stores/user'
import type { PackageItem } from '~/api/api'
import { emitOrderGoods } from '~/utils/emitter'
import { useLocalePath } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { InfoFilled } from '@element-plus/icons-vue'
import modelRatesData from '~/assets/data/model-rates.json'

const { t, locale } = useI18n()

// 使用多语言 SEO
useMultilingualSeo({ pageType: 'apiPricing', robots: 'noindex, follow' })

// 结构化数据
const requestUrlHref = useRequestURL().href

useHead({
    script: [
        // BreadcrumbList Schema
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'WeryAI',
                            item: 'https://www.weryai.com',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.pricingTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

const localePath = useLocalePath()

// API store
const apiStore = useApiStore()
const userStore = useUserStore()

// 获取API余额，格式化为千分位
const userCredits = computed(() => apiStore.formattedBalance)

// 获取商品列表
const packages = computed(() => apiStore.packageList)

// ============ 模型费率对照表 ============
interface VideoRate {
    model: string
    title: string
    typeKey: 't2v' | 'i2v'
    brand: string
    mode: string
    duration: number | null
    resolution: string
    credits: number
}
interface ImageRate {
    model: string
    title: string
    typeKey: 't2i' | 'i2i'
    brand: string
    quality: string
    ratio: string
    resolution: string
    credits: number
}
interface Span {
    show: boolean
    span: number
}

const videoRates = modelRatesData.video as VideoRate[]
const imageRates = modelRatesData.image as ImageRate[]

// 当前费率标签页
const activeRateTab = ref<'video' | 'image'>('video')

// 品牌筛选(空串表示“全部”)
const activeBrand = ref('')

// 当前分类下可选的品牌(按首次出现顺序去重)
const availableBrands = computed(() => {
    const src = activeRateTab.value === 'video' ? videoRates : imageRates
    const seen = new Set<string>()
    const list: string[] = []
    for (const r of src) {
        if (!seen.has(r.brand)) {
            seen.add(r.brand)
            list.push(r.brand)
        }
    }
    return list
})

// 切换视频/图片时重置品牌筛选为“全部”
watch(activeRateTab, () => {
    activeBrand.value = ''
})

// 类型文案
const typeText = (key: string): string => {
    const map: Record<string, string> = {
        t2v: t('api.rateTypeT2v'),
        i2v: t('api.rateTypeI2v'),
        t2i: t('api.rateTypeT2i'),
        i2i: t('api.rateTypeI2i'),
    }
    return map[key] || key
}

// 模式文案
const modeText = (mode: string): string => {
    if (mode === 'audio') return t('api.rateModeAudio')
    return t('api.rateModeDefault')
}

// 单位单复数：仅对有单复数区分的语言，在数量恰为 1 时用单数形式。
// 未列出的语言（中文/日文等无单复数概念，或规则复杂待确认）一律用 creditsUnit。
const CREDITS_UNIT_SINGULAR: Record<string, string> = {
    en: 'credit',
}

// 消耗积分文案
const formatCredits = (credits: number): string => {
    const n = Number(credits)
    const num = n.toFixed(2)
    const singular = CREDITS_UNIT_SINGULAR[locale.value]
    const unit = n === 1 && singular ? singular : t('api.creditsUnit')
    return `${num} ${unit}`
}

/**
 * 计算某一分组字段的 rowspan 信息。
 * keyFn 返回该行在当前层级的“分组键”（包含所有上层键，保证分组边界）。
 * 返回每行的 { show, span }：show=true 表示该单元格是分组首行需要渲染。
 */
const buildSpans = <T,>(rows: T[], keyFn: (row: T) => string): Span[] => {
    const keys = rows.map(keyFn)
    return keys.map((key, i) => {
        const isFirst = i === 0 || keys[i - 1] !== key
        if (!isFirst) return { show: false, span: 0 }
        let span = 1
        while (i + span < keys.length && keys[i + span] === key) span++
        return { show: true, span }
    })
}

/**
 * 按标题首次出现顺序稳定排序，保证同一标题的所有行连续，
 * 便于 rowspan 分组。keyFns 依次作为组内排序依据。
 */
const groupByTitle = <T extends { title: string }>(
    src: T[],
    keyFns: Array<(row: T) => string | number>
): T[] => {
    const titleOrder = new Map<string, number>()
    src.forEach(r => {
        if (!titleOrder.has(r.title)) titleOrder.set(r.title, titleOrder.size)
    })
    return src
        .map((row, idx) => ({ row, idx }))
        .sort((a, b) => {
            const ta = titleOrder.get(a.row.title) ?? 0
            const tb = titleOrder.get(b.row.title) ?? 0
            if (ta !== tb) return ta - tb
            for (const fn of keyFns) {
                const va = fn(a.row)
                const vb = fn(b.row)
                if (va < vb) return -1
                if (va > vb) return 1
            }
            return a.idx - b.idx
        })
        .map(x => x.row)
}

// 分辨率排序权重（兼容 768p、2k、4k 等不同命名，'-' 排最后）
const resRank = (res: string) => {
    const normalized = res.trim().toLowerCase()
    const kMatch = /^(\d+(?:\.\d+)?)k$/.exec(normalized)
    if (kMatch) return Number(kMatch[1]) * 1000
    const m = /^(\d+)/.exec(res)
    return m ? Number(m[1]) : 9999
}

// 视频分辨率展示文案：统一 K 档大小写；4320p 沿用现有 8K 文案
const resolutionText = (res: string): string => {
    if (res === '4320p') return '8K'
    if (/^\d+(?:\.\d+)?k$/i.test(res)) return res.toUpperCase()
    return res
}

// 质量排序权重（空串无质量排最前）
const qualityRank = (q: string) => {
    const map: Record<string, number> = {
        low: 1,
        medium: 2,
        high: 3,
        xhigh: 4,
        max: 5,
    }
    return map[q] ?? 0
}

// 质量文案（空串→'—'）
const qualityText = (q: string): string => {
    const map: Record<string, string> = {
        low: t('api.rateQualityLow'),
        medium: t('api.rateQualityMedium'),
        high: t('api.rateQualityHigh'),
        xhigh: t('api.rateQualityXhigh'),
        max: t('api.rateQualityMax'),
    }
    return map[q] || '—'
}

// 视频费率行（含 rowspan 分组：标题 > 模式 > 时长 > 分辨率，类型为最内层叶子）
const videoRateRows = computed(() => {
    const src = activeBrand.value
        ? videoRates.filter(r => r.brand === activeBrand.value)
        : videoRates
    const rows = groupByTitle(src, [
        r => (r.mode === 'default' ? 0 : 1),
        r => r.duration ?? 0,
        r => resRank(r.resolution),
        r => (r.typeKey === 'i2v' ? 0 : 1),
    ])
    const titleSpans = buildSpans(rows, r => r.title)
    const modeSpans = buildSpans(rows, r => `${r.title}|${r.mode}`)
    const durSpans = buildSpans(rows, r => `${r.title}|${r.mode}|${r.duration}`)
    const resSpans = buildSpans(
        rows,
        r => `${r.title}|${r.mode}|${r.duration}|${r.resolution}`
    )
    return rows.map((row, idx) => ({
        row,
        spans: {
            title: titleSpans[idx]!,
            mode: modeSpans[idx]!,
            duration: durSpans[idx]!,
            resolution: resSpans[idx]!,
        },
    }))
})

// 图片费率行（含 rowspan 分组：标题 > 类型 > 质量 > 比例，分辨率为最内层叶子）
const imageRateRows = computed(() => {
    const src = activeBrand.value
        ? imageRates.filter(r => r.brand === activeBrand.value)
        : imageRates
    const rows = groupByTitle(src, [
        r => (r.typeKey === 't2i' ? 0 : 1),
        r => qualityRank(r.quality),
        r => r.ratio,
        r => resRank(r.resolution),
    ])
    const titleSpans = buildSpans(rows, r => r.title)
    const typeSpans = buildSpans(rows, r => `${r.title}|${r.typeKey}`)
    const qualitySpans = buildSpans(
        rows,
        r => `${r.title}|${r.typeKey}|${r.quality}`
    )
    const ratioSpans = buildSpans(
        rows,
        r => `${r.title}|${r.typeKey}|${r.quality}|${r.ratio}`
    )
    return rows.map((row, idx) => ({
        row,
        spans: {
            title: titleSpans[idx]!,
            typeKey: typeSpans[idx]!,
            quality: qualitySpans[idx]!,
            ratio: ratioSpans[idx]!,
        },
    }))
})

// 加载状态
const loading = ref(false)

/**
 * 加载页面数据
 * 如果 store 中有缓存数据，则不重复调用接口
 */
const fetchPricingData = async () => {
    try {
        loading.value = true

        // 费率表使用本地静态数据；未登录时不请求余额，避免认证拦截器自动打开登录弹窗。
        await Promise.all([
            apiStore.fetchPackages(),
            ...(userStore.isLoggedIn ? [apiStore.fetchBalance()] : []),
        ])
    } catch (error) {
        console.error('获取定价信息失败:', error)
    } finally {
        loading.value = false
    }
}

/**
 * 购买指定套餐
 * @param pkg 要购买的套餐
 */
const handlePurchase = (pkg: PackageItem) => {
    if (!pkg) {
        console.error('未找到要购买的套餐')
        return
    }

    // 调用全局订单事件，触发支付流程
    // API 订单通过 originalData 传递完整的商品信息
    emitOrderGoods(pkg.originalData)

    // 支付完成后可以刷新余额
    // 这里可以监听支付成功事件来刷新余额
}

// 页面加载时获取数据
onMounted(() => {
    fetchPricingData()
})
</script>

<style scoped lang="less">
// 状态指示点 - 需要特殊的box-shadow效果
.status-dot {
    width: 7px;
    height: 7px;
    background: #99f768;
    border-radius: 50%;
    box-shadow: 0px 0px 10px 0px rgba(163, 230, 53, 0.5);
}

// 骨架屏流光动画
.skeleton-shimmer {
    background: linear-gradient(
        90deg,
        rgba(39, 39, 42, 0.4) 0%,
        rgba(63, 63, 70, 0.4) 50%,
        rgba(39, 39, 42, 0.4) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

// 套餐卡片 - 2 列网格布局，每张卡片独立购买
.package-card {
    position: relative;
    display: flex;
    flex-direction: column;
    background: #1c1c20;
    border: 1px solid #27272a;
    border-radius: 14px;
    padding: 24px;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    @media (max-width: 768px) {
        padding: 18px;
        border-radius: 12px;
    }

    // hover 高亮，沿用主题绿；按钮同步变主题绿实心
    &:hover {
        border-color: #99f768;
        box-shadow: 0px 0px 0px 1px rgba(163, 230, 53, 0.35);

        .package-buy-btn {
            color: #000;
            background: #99f768;

            &:hover {
                background: rgba(153, 247, 104, 0.7);
            }
        }
    }

    // 默认选中套餐高亮，沿用主题绿
    &.featured {
        border-color: #99f768;
        box-shadow: 0px 0px 0px 1px rgba(163, 230, 53, 0.6);

        // 移动端单列布局时，默认高亮卡片排到最前
        @media (max-width: 768px) {
            order: -1;
        }
    }

    // 积分行 - 与总价同行，右对齐
    .package-credits-inline {
        display: flex;
        align-items: baseline;
        gap: 5px;
        line-height: 1.4;
        color: #a1a1aa;
        white-space: nowrap;

        // 积分数字 - 保持较大
        .package-credits-value {
            font-size: 18px;

            @media (max-width: 768px) {
                font-size: 16px;
            }
        }

        // "积分"文案 - 调小
        .package-credits-label {
            font-size: 13px;

            @media (max-width: 768px) {
                font-size: 12px;
            }
        }
    }

    // 说明行 - 数值 + 标签
    .package-info-line {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13.5px;
        line-height: 1.4;
        color: #a1a1aa;

        @media (max-width: 768px) {
            font-size: 12.5px;
        }

        // 约等于图片数说明图标
        .package-info-tooltip-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 14px;
            height: 14px;
            color: #71717a;
            font-size: 14px;
            cursor: help;
            transition:
                color 0.2s,
                filter 0.2s;

            &:hover,
            &:focus-visible {
                color: #99f768;
                filter: drop-shadow(0 0 5px rgba(153, 247, 104, 0.35));
                outline: none;
            }
        }
    }

    // 购买按钮 - 默认深色，高亮卡片为主题绿实心
    .package-buy-btn {
        margin-top: auto;
        width: 100%;
        border: none;
        border-radius: 999px;
        padding: 12px 20px;
        font-family: 'PingFang SC', sans-serif;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
        color: #fff;
        background: #27272a;
        cursor: pointer;
        transition:
            background 0.2s,
            opacity 0.2s;

        &:hover {
            background: #3f3f46;
        }

        &.is-featured {
            color: #000;
            background: #99f768;

            &:hover {
                background: rgba(153, 247, 104, 0.7);
            }
        }
    }
}

// hover 其他卡片时，默认选中卡片取消高亮，仅高亮当前 hover 的卡片
.packages-grid:has(.package-card:hover) .package-card.featured:not(:hover) {
    border-color: #27272a;
    box-shadow: none;

    .package-buy-btn.is-featured {
        color: #fff;
        background: #27272a;

        &:hover {
            background: #3f3f46;
        }
    }
}

.enterprise-pricing {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 32px;
    padding: 25px 28px;
    overflow: hidden;
    border: 1px solid rgba(153, 247, 104, 0.28);
    border-radius: 12px;
    background: #161a16;

    &::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 3px;
        content: '';
        background: #99f768;
    }

    @media (max-width: 768px) {
        align-items: stretch;
        flex-direction: column;
        gap: 20px;
        margin-top: 20px;
        margin-bottom: 0;
        padding: 21px 18px 18px;
        border-radius: 10px;
    }
}

.enterprise-pricing-copy {
    min-width: 0;
}

.enterprise-pricing-eyebrow {
    margin: 0 0 7px;
    color: #99f768;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.4;
    text-transform: uppercase;
}

.enterprise-pricing h3 {
    margin: 0;
    color: #fff;
    font-size: 18px;
    font-weight: 650;
    line-height: 1.45;

    @media (max-width: 768px) {
        font-size: 16px;
    }
}

.enterprise-pricing-desc {
    max-width: 640px;
    margin: 6px 0 0;
    color: #a1a1aa;
    font-size: 13px;
    line-height: 1.65;

    @media (max-width: 768px) {
        font-size: 12px;
    }
}

.enterprise-pricing-benefits {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 18px;
    margin-top: 14px;

    span {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: #d4d4d8;
        font-size: 12px;
        line-height: 1.4;

        &::before {
            width: 4px;
            height: 4px;
            content: '';
            border-radius: 50%;
            background: #99f768;
            box-shadow: 0 0 7px rgba(153, 247, 104, 0.55);
        }
    }
}

.enterprise-pricing-link {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 20px;
    border-radius: 8px;
    color: #080b08;
    background: #99f768;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.4;
    text-decoration: none;
    transition:
        background 0.2s ease,
        transform 0.2s ease;

    &:hover {
        background: #adff82;
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 3px;
    }

    @media (max-width: 768px) {
        width: 100%;
    }
}

// 模型费率标题提示
.model-rates-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
}

.model-rates-tooltip-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: #a1a1aa;
    font-size: 18px;
    cursor: help;
    transition:
        color 0.2s,
        filter 0.2s;

    &:hover,
    &:focus-visible {
        color: #99f768;
        filter: drop-shadow(0 0 6px rgba(153, 247, 104, 0.35));
        outline: none;
    }
}

:global(.model-rates-tooltip-popper) {
    max-width: min(420px, calc(100vw - 40px));
    padding: 12px 14px;
    border: 1px solid rgba(153, 247, 104, 0.22) !important;
    border-radius: 10px !important;
    background: rgba(9, 9, 11, 0.96) !important;
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.38);

    @media (max-width: 768px) {
        max-width: min(320px, calc(100vw - 28px));
    }
}

:global(.model-rates-tooltip-popper .el-popper__arrow::before) {
    border-color: rgba(153, 247, 104, 0.22) !important;
    background: rgba(9, 9, 11, 0.96) !important;
}

.model-rates-tooltip-content {
    color: #d4d4d8;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.55;

    p {
        margin: 0;

        & + p {
            margin-top: 6px;
        }
    }
}

// 表格样式
table {
    thead {
        th {
            font-size: 10.5px;
            font-weight: 600;
            text-transform: capitalize;
            line-height: 12.5px;
            color: #a1a1aa;
            padding: 14.5px 21px;
            text-align: left;

            @media (max-width: 768px) {
                font-family: 'PingFang SC', sans-serif;
                font-size: 10px;
                font-weight: 600;
                line-height: 1.4em;
                padding: 15px 17.5px 15.5px;
            }
        }
    }

    tbody {
        td {
            font-family: 'Inter', sans-serif;
            font-size: 12.3px;
            font-weight: 400;
            line-height: 15px;
            padding: 15.5px 21px;

            @media (max-width: 768px) {
                font-size: 12px;
                line-height: 1.21em;
                padding: 15.5px 17.5px;
            }

            &.model-name {
                font-weight: 500;
                color: #d4d4d8;
            }

            &.model-consumption {
                font-family: 'PingFang SC', sans-serif;
                color: #d4d4d8;
                background: rgba(24, 24, 27, 0.3);
                white-space: nowrap;

                @media (max-width: 768px) {
                    font-size: 12px;
                    line-height: 1.4em;
                }
            }

            // rowspan 分组单元格：顶部对齐并以分隔线区分分组
            &.rate-group-cell {
                vertical-align: top;
                color: #d4d4d8;
                border-left: 1px solid #27272a;
                background: rgba(24, 24, 27, 0.2);
            }
        }
    }
}

// 视频/图片费率切换标签
.rate-tabs {
    position: relative;
    display: flex;
    width: fit-content;
    padding: 4px;
    margin: 0 auto 16px;
    border-radius: 999px;
    background: rgba(24, 24, 27, 0.6);
    border: 1px solid #27272a;

    // 切到"图片"时高亮块滑到右侧
    &.is-image .rate-tab-slider {
        transform: translateX(100%);
    }
}

.rate-tab-slider {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 4px;
    width: calc(50% - 4px);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
}

.rate-tab {
    position: relative;
    z-index: 1;
    flex: 1 0 auto;
    min-width: 72px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
    text-align: center;
    color: #a1a1aa;
    padding: 8px 18px;
    background: transparent;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    transition: color 0.28s ease;

    &:hover {
        color: #d4d4d8;
    }

    &.is-active {
        color: #fff;
        font-weight: 600;
    }

    @media (max-width: 768px) {
        font-size: 12px;
        padding: 7px 15px;
    }
}

// 品牌 / 产品线筛选标签
.brand-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
        flex-wrap: nowrap;
        overflow-x: auto;
        margin: 0 -20px 14px;
        padding: 0 20px 4px;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            display: none;
        }
    }
}

.brand-tab {
    flex: 0 0 auto;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1;
    color: #a1a1aa;
    padding: 7px 12px;
    white-space: nowrap;
    background: transparent;
    border: none;
    border-radius: 7px;
    cursor: pointer;
    transition:
        color 0.2s ease,
        background 0.2s ease;

    &:hover {
        color: #e4e4e7;
    }

    &.is-active {
        color: #fff;
        font-weight: 600;
        background: rgba(255, 255, 255, 0.1);
    }

    @media (max-width: 768px) {
        font-size: 12px;
        padding: 6px 10px;
    }
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
